import { useState, useEffect } from 'react';
import { useAppStore } from '../store';
import { motion } from 'framer-motion';
import { ClipboardCheck, Clock, Award, Play, Pause, RotateCcw, Eye, EyeOff, CheckCircle } from 'lucide-react';

export default function TestsPage() {
  const { questions, activeSubjectId, units } = useAppStore();
  const subjectQuestions = questions.filter(q => q.subjectId === activeSubjectId);
  const subjectUnits = units.filter(u => u.subjectId === activeSubjectId);
  const [testStarted, setTestStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(45 * 60);
  const [isPaused, setIsPaused] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [currentAnswers, setCurrentAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!testStarted || isPaused || timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [testStarted, isPaused, timeLeft]);

  const totalMarks = subjectQuestions.reduce((sum, q) => sum + q.marks, 0);
  const formatTime = (s: number) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const testInfo = {
    title: `${subjectUnits[0]?.title || '单元'} 模拟测验`,
    duration: 45,
    totalMarks,
    structure: [
      { section: 'Section A', type: '简答题 Short Answer', marks: subjectQuestions.filter(q => q.tier === 'knowledge').reduce((s, q) => s + q.marks, 0), time: '15 min' },
      { section: 'Section B', type: '结构题 Structured', marks: subjectQuestions.filter(q => q.tier === 'application' || q.tier === 'analysis').reduce((s, q) => s + q.marks, 0), time: '15 min' },
      { section: 'Section C', type: '论述题 Extended Response', marks: subjectQuestions.filter(q => q.tier === 'evaluation').reduce((s, q) => s + q.marks, 0), time: '15 min' },
    ],
    gradeBoundaries: [
      { grade: 7, min: 85 }, { grade: 6, min: 72 }, { grade: 5, min: 58 },
      { grade: 4, min: 45 }, { grade: 3, min: 32 }, { grade: 2, min: 20 }, { grade: 1, min: 0 },
    ]
  };

  if (!testStarted) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">模拟测试</h1>
          <p className="text-sm text-stone-400 mt-2 font-body">模拟 IB 校内测验环境，含完整评分标准</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-5 sm:p-7">
          <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-7">
            <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-bronze to-bronze-light flex items-center justify-center shadow-md flex-shrink-0">
              <ClipboardCheck className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-display text-stone-950">{testInfo.title}</h2>
              <div className="flex items-center gap-4 mt-1.5">
                <span className="flex items-center gap-1 text-xs text-stone-400 font-body"><Clock className="w-3 h-3" />{testInfo.duration} 分钟</span>
                <span className="flex items-center gap-1 text-xs text-stone-400 font-body"><Award className="w-3 h-3" />{testInfo.totalMarks} 分</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-5 sm:mb-7">
            <h3 className="text-sm font-semibold text-stone-950 font-body">试卷结构</h3>
            {testInfo.structure.map(section => (
              <div key={section.section} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-0 p-3 sm:p-3.5 rounded-xl bg-linen">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-stone-950 w-20 font-body">{section.section}</span>
                  <span className="text-xs text-stone-500 font-body">{section.type}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-stone-400 font-body">{section.time}</span>
                  <span className="text-xs font-semibold text-stone-950 font-body">{section.marks} 分</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mb-5 sm:mb-7">
            <h3 className="text-sm font-semibold text-stone-950 mb-3 font-body">等级分数线 Grade Boundaries</h3>
            <div className="grid grid-cols-4 sm:flex gap-2">
              {testInfo.gradeBoundaries.map(gb => (
                <div key={gb.grade} className="flex-1 text-center p-2.5 rounded-xl bg-linen">
                  <p className="text-lg font-display text-stone-950">{gb.grade}</p>
                  <p className="text-[10px] text-stone-400 font-body">≥{gb.min}%</p>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => setTestStarted(true)}
            className="w-full py-3.5 rounded-2xl btn-primary text-sm font-semibold flex items-center justify-center gap-2 font-body"
          >
            <Play className="w-4 h-4" /> 开始测验
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Timer bar */}
      <div className="sticky top-0 z-10 glass rounded-2xl border border-stone-200/50 p-3 sm:p-3.5 flex items-center justify-between gap-2">
        <h2 className="text-xs sm:text-sm font-semibold text-stone-950 font-body truncate">{testInfo.title}</h2>
        <div className="flex items-center gap-3">
          <span className={`text-lg font-mono font-bold tabular-nums ${timeLeft < 300 ? 'text-terracotta' : 'text-stone-950'}`}>
            {formatTime(timeLeft)}
          </span>
          <button onClick={() => setIsPaused(!isPaused)} className="p-2 rounded-xl hover:bg-stone-100 transition-colors">
            {isPaused ? <Play className="w-4 h-4 text-stone-600" /> : <Pause className="w-4 h-4 text-stone-600" />}
          </button>
          <button onClick={() => setShowResults(!showResults)} className="p-2 rounded-xl hover:bg-stone-100 transition-colors">
            {showResults ? <EyeOff className="w-4 h-4 text-stone-600" /> : <Eye className="w-4 h-4 text-stone-600" />}
          </button>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {subjectQuestions.map((q, i) => (
          <motion.div
            key={q.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="card-premium p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-stone-950 font-body">题目 {i + 1}</span>
              <span className="text-xs text-stone-400 font-body">[{q.marks} marks]</span>
            </div>
            <p className="text-sm text-stone-950 leading-relaxed whitespace-pre-line mb-4 font-body">{q.content}</p>
            <textarea
              value={currentAnswers[q.id] || ''}
              onChange={(e) => setCurrentAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
              placeholder="在此作答..."
              className="w-full min-h-[80px] p-3.5 text-xs rounded-xl border border-stone-200 focus:border-bronze focus:ring-2 focus:ring-bronze/10 outline-none resize-y transition-all bg-linen font-body"
            />
            {showResults && (
              <div className="mt-3 space-y-2">
                <div className="bg-sage-50 rounded-xl p-4">
                  <p className="text-[10px] font-semibold text-sage-dark mb-1.5 flex items-center gap-1 font-body"><CheckCircle className="w-3 h-3" /> 参考答案</p>
                  <p className="text-xs text-stone-600 font-body">{q.answer}</p>
                </div>
                <div className="bg-steel-50 rounded-xl p-4">
                  <p className="text-[10px] font-semibold text-steel mb-1.5 font-body">评分思路</p>
                  <p className="text-xs text-stone-600 font-body">{q.markScheme}</p>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <button
        onClick={() => { setTestStarted(false); setTimeLeft(45 * 60); setCurrentAnswers({}); setShowResults(false); }}
        className="w-full py-3.5 rounded-2xl border border-stone-200 text-sm font-medium hover:bg-linen transition-colors flex items-center justify-center gap-2 text-stone-600 font-body"
      >
        <RotateCcw className="w-4 h-4" /> 重新开始
      </button>
    </div>
  );
}
